import { createContext, useState, useContext, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react'; // Clerk authentication
import { checkYouTubeLogin } from '../utils/apiCalls'; // Assuming fetchSalesData is defined here

// Create a context for the Revit application
const RevitContext = createContext();

export const RevitProvider = ({ children }) => {
    const { isSignedIn, user, isLoaded } = useUser();
    const { getToken } = useAuth();
    const [username, setUsername] = useState(null);
    const [youtubeName, setYoutubeName] = useState(null);
    const [youtubeThumbUrl, setYoutubeThumbUrl] = useState(null);
    const [userEmail, setUserEmail] = useState(null);
    const freeUsernames = ['user_2o7DMQGJQxprgVg3kd1BqHcJJeO', 'user_2o7bLOJ3puECQcBuHxznWsXLbyI', 'user_2n0ZoEM9RQhpYgfpcp6Cvsufxi8', 'user_2oO8YZC1DlUsnKu1AjqRVnNLyf9', 'user_2oO9NK3nXEPIWFgRaJ2jfEZEmxr', 'user_2oOC97YHcFkcK2ufzdHPy0WWwjS', 'user_2oOL04gF5vg5q7qdsOz3p9DFjyj', 'user_2oP7GYapyiHwzt6jCYUGXmFvKeW', 'user_2oT3RFarmHFURN2jy786ZqyZRwG', 'user_2oTyojm2Csb3TcuzEEGPn1EEeD1', 'user_2olR4i6dgeIgVKaJt1IebzAgDnM', 'user_2ouFoIfMQZY2Jnkiw7ghEraXQGI', 'user_2p3S1466XnJ8mSWDZworTvoiSQG', 'user_2p9iRFLRzYry3cn03lMvXjuyWKL', 'user_2pR8VlL0MBCR3PmY0q9TjlNLxkQ', 'user_2r2muq6vhGP6VpZ7n9FfdiqAi1F', 'user_3LOvmnPBo6cZ5DzdoPGJpCOpbaB'];
    const [isSecondaryUser, setIsSecondaryUser] = useState(false);
    const [subAccounts, setSubAccounts] = useState([]);
    const [hasSubAccounts, setHasSubAccounts] = useState(false);
    const [isSubAccountActive, setIsSubAccountActive] = useState(false);
    const [activeSubAccountName, setActiveSubAccountName] = useState('');
    const [youtubeLoading, setYoutubeLoading] = useState(true);
    const [subAccountAccess, setSubAccountAccess] = useState([]);
    const [primaryUserAccounts, setPrimaryUserAccounts] = useState([]);
    const [isProModalOpen, setIsProModalOpen] = useState(false);

    // Custom setter for isSubAccountActive that also updates localStorage
    const setSubAccountActive = (isActive) => {
        setIsSubAccountActive(isActive);
        console.log('Setting isSubAccountActive to:', isActive);
        localStorage.setItem('isSubAccountActive', isActive ? 'true' : 'false');
    };

    // Custom setter for username that also updates localStorage when it's a sub-account
    const setUsernameWithStorage = (newUsername) => {
        console.log('Updating username to:', newUsername);
        setUsername(newUsername);
        localStorage.setItem('activeSubAccountUsername', newUsername);
    };

    // Custom setter for active sub account name
    const setActiveSubAccountNameWithStorage = (name) => {
        setActiveSubAccountName(name);
        localStorage.setItem('activeSubAccountName', name);
    };

    // Function to fetch sub-account access for secondary users
    const fetchSubAccountAccess = async (secondaryUserId) => {
        try {
            const token = await getToken();
            const response = await fetch(`https://${process.env.REACT_APP_SERVER_URL}/api/get-sub-account-access/${secondaryUserId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setSubAccountAccess(data.subAccountAccess || null);
                return data.subAccountAccess || null;
            }
        } catch (error) {
            console.error('Error fetching sub-account access:', error);
            setSubAccountAccess(null);
            return [];
        }
    };

    // Function to fetch and set the username
    useEffect(() => {
        const fetchUser = async () => {
            if (isLoaded && isSignedIn) {
                // Check account mode preference from localStorage
                const accountMode = localStorage.getItem('accountMode');
                console.log(user.publicMetadata.secondaryUser);
                
                // Check if there's a stored active sub-account
                const storedIsSubAccountActive = localStorage.getItem('isSubAccountActive') === 'true';
                const storedSubAccountUsername = localStorage.getItem('activeSubAccountUsername');
                const storedSubAccountName = localStorage.getItem('activeSubAccountName');
                
                // For primary users, check if we should restore a sub-account
                if (storedIsSubAccountActive && storedSubAccountUsername) {
                    setIsSubAccountActive(true);
                    setUsername(storedSubAccountUsername);
                    if (storedSubAccountName) {
                        setActiveSubAccountName(storedSubAccountName);
                    }
                }
                
                // Check if this is a secondary user
                if (user.publicMetadata && user.publicMetadata.secondaryUser) {
                    // Extract all secondary user accounts from metadata
                    const primaryAccounts = [];
                    const metadata = user.publicMetadata;
                    
                    // Check for secondaryUser
                    if (metadata.secondaryUser) {
                        primaryAccounts.push(metadata.secondaryUser);
                    }
                    
                    // Check for secondaryUser2, secondaryUser3, etc.
                    let counter = 2;
                    while (metadata[`secondaryUser${counter}`]) {
                        primaryAccounts.push(metadata[`secondaryUser${counter}`]);
                        counter++;
                    }
                    
                    setPrimaryUserAccounts(primaryAccounts);
                    console.log('Found primary user accounts:', primaryAccounts);
                    
                    // Set username to the primary user's ID (team mode)
                    setIsSecondaryUser(true);
                    if (accountMode !== 'personal') {
                        // Fetch sub-account access for this secondary user
                        const accessList = await fetchSubAccountAccess(user.id);
                        console.log('Access list:', accessList);
                        
                        // Check if main account access is included
                        if (accessList && !accessList.includes('main')) {
                            // Find first accessible sub-account from the access list
                            const firstAccessibleSubAccount = accessList.find(accountUsername => 
                                accountUsername !== 'main' // Skip 'main' since we know it's not included
                            );
                            
                            if (firstAccessibleSubAccount) {
                                console.log('Switching to first accessible sub-account:', firstAccessibleSubAccount);
                                setSubAccountActive(true);
                                setUsernameWithStorage(firstAccessibleSubAccount);
                                setUsername(firstAccessibleSubAccount);
                                // Note: activeSubAccountName will be set later when subAccounts are loaded
                            } else {
                                // Get active primary user from localStorage or default to first one
                                const activePrimaryUser = localStorage.getItem('activePrimaryUser') || primaryAccounts[0];
                                setUsername(activePrimaryUser);
                            }
                        } else {
                            // Get active primary user from localStorage or default to first one
                            const activePrimaryUser = localStorage.getItem('activePrimaryUser') || primaryAccounts[0];
                            setUsername(activePrimaryUser);
                        }
                    } else {
                        setUsername(user.id);
                    }
                } else {
                    // For primary users, if no sub-account was restored, use personal account
                    if (!storedIsSubAccountActive || !storedSubAccountUsername) {
                        setUsername(user.id);
                        setIsSubAccountActive(false);
                    }
                    
                    setIsSecondaryUser(false);
                }
                
                setUserEmail(user.primaryEmailAddress.emailAddress);
            }
        };
        
        // Initial fetch
        fetchUser();
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, isSignedIn, user]);

    useEffect(() => {
        const fetchData = async () => {
            const token = await getToken();
            if (username) {
                setYoutubeLoading(true);
                const data = await checkYouTubeLogin(username, token);
                if (data && data.loggedIn) {
                    setYoutubeName(data.youtubeName);
                    setYoutubeThumbUrl(data.thumbnailUrl);
                } else {
                    // Clear YouTube data if not logged in with current account
                    setYoutubeName(null);
                    setYoutubeThumbUrl(null);
                }
                console.log('YouTube data fetched:', data);
                setYoutubeLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username]);

    return (
        <RevitContext.Provider value={{ 
            username, 
            setUsername: setUsernameWithStorage, 
            userEmail, 
            isSecondaryUser, 
            freeUsernames, 
            youtubeName, 
            setYoutubeName, 
            youtubeThumbUrl, 
            setYoutubeThumbUrl, 
            youtubeLoading,
            subAccounts, 
            setSubAccounts, 
            hasSubAccounts, 
            setHasSubAccounts,
            isSubAccountActive,
            setIsSubAccountActive: setSubAccountActive,
            activeSubAccountName,
            setActiveSubAccountName: setActiveSubAccountNameWithStorage,
            subAccountAccess,
            setSubAccountAccess,
            primaryUserAccounts,
            isProModalOpen,
            setIsProModalOpen
        }}>
            {children}
        </RevitContext.Provider>
    );
};

// Custom hook to use the Revit context
export const useRevit = () => {
    return useContext(RevitContext);
};
